import { Auth } from "aws-amplify";
import { API_PREFIX } from "../../consts/api-endpoints";

/*
 * Support-staff impersonation (read-only view of a customer account).
 *
 * The backend's customer authorizer only sees the Authorization value, so while a session is
 * active every API call sends `imp_<sessionId>:<cognito access token>`; /admin/* calls (session
 * management) always go out with the staff member's own token.
 *
 * The session lives in sessionStorage: per tab, gone when the tab closes.
 */
const STORAGE_KEY = 'bc:impersonation';
export const IMPERSONATION_TOKEN_PREFIX = 'imp_';
export const SUPPORT_GROUP = 'bc-support';
export const ADMIN_ENDPOINT = `${API_PREFIX}/admin`;

function readStored() {
  try {
    const session = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
    return session && session.sessionId ? session : null;
  } catch (_) {
    return null;
  }
}

export function clearImpersonation() {
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) { }
}

/** The active session, or null. An expired session is dropped. */
export function getImpersonation() {
  const session = readStored();
  if (!session) return null;
  if (!session.expiresAt || session.expiresAt * 1000 <= Date.now()) {
    clearImpersonation();
    return null;
  }
  return session;
}

/** The stored session even when expired (to end it server-side). */
export function getStoredImpersonation() {
  return readStored();
}

export function isImpersonating() {
  return !!getImpersonation();
}

/** Authorization value for a request to `url` made by `username` with `token`. */
export function applyImpersonation(token, url, username) {
  const session = getImpersonation();
  if (!session || !token || (url || '').startsWith(ADMIN_ENDPOINT)) {
    return token;
  }
  if (username && session.actorId && session.actorId !== username) {
    // left behind by another login in this tab
    clearImpersonation();
    return token;
  }
  return `${IMPERSONATION_TOKEN_PREFIX}${session.sessionId}:${token}`;
}

export async function isSupportStaff() {
  try {
    const session = await Auth.currentSession();
    const groups = session.getAccessToken().payload['cognito:groups'] || [];
    return groups.includes(SUPPORT_GROUP);
  } catch (_) {
    return false;
  }
}

/** Stores the session and reloads, so no state from the staff member's own account survives. */
export function beginImpersonation(session) {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  window.location.assign('/');
}
